import { Component, OnInit, OnDestroy, Input, Output, ViewChild, EventEmitter, ChangeDetectionStrategy } from "@angular/core";
import { NgForm, Validators, FormBuilder } from '@angular/forms';
import { Subscription } from 'rxjs';

import { AlertService, MessageSeverity, DialogType } from '../../services/alert.service';
import { AuthService } from "../../services/auth.service";
import { ConfigurationService } from '../../services/configuration.service';
import { Utilities } from '../../services/utilities';
import { UserLogin } from '../../models/user-login.model';

@Component({
    selector: "app-login-control",
    templateUrl: './login-control.component.html',
    styleUrls: ['./login-control.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LoginControlComponent implements OnInit, OnDestroy {

    isLoading = false;
    formResetToggle = true;
    modalClosedCallback: { (): void } | undefined;
    loginStatusSubscription: Subscription | undefined;


    @ViewChild('form', { static: true })
    private form!: NgForm;

    @Input() isModal = false;

    @Output() enterKeyPress = new EventEmitter();

    loginForm = this.fb.nonNullable.group({
            userName: ['', Validators.required],
            password: ['', Validators.required],
            rememberMe: false as boolean
        });

    constructor(
        private alertService: AlertService,
        private authService: AuthService,
        private configurations: ConfigurationService,
        private fb: FormBuilder) {
    }

    ngOnInit() {
        this.loginForm.patchValue({
            rememberMe: this.authService.rememberMe
        });

        if (this.getShouldRedirect()) {
            this.authService.redirectLoginUser();
        }
        else {
            this.loginStatusSubscription = this.authService.getLoginStatusEvent()
                .subscribe(() => {
                    if (this.getShouldRedirect()) {
                        this.authService.redirectLoginUser();
                    }
                });
        }
    }

    ngOnDestroy() {
        this.loginStatusSubscription?.unsubscribe();
    }


    getShouldRedirect() {
        return !this.isModal && this.authService.isLoggedIn && !this.authService.isSessionExpired;
    }

    closeModal() {
        if (this.modalClosedCallback) {
            this.modalClosedCallback();
        }
    }

    getUserLogin(): UserLogin {
        const formModel = this.loginForm.value;
        return new UserLogin(formModel.userName, formModel.password, formModel.rememberMe);
    }

    login() {
        if (!this.form.submitted) {
            this.form.onSubmit(null as unknown as Event);
            return;
        }

        if (!this.loginForm.valid) {
            this.alertService.showValidationError();
            return;
        }

        this.isLoading = true;
        this.alertService.startLoadingMessage("", "Försöker logga in...");

        this.authService.loginWithPassword(this.getUserLogin())
            .subscribe({
            next: user => {
                setTimeout(() => {
                    this.alertService.stopLoadingMessage();
                    this.isLoading = false;
                    this.loginForm.reset();

                    if (!this.isModal) {
                        this.alertService.showMessage("Logga in", `Välkommen ${user.userName}!`, MessageSeverity.success);
                    }
                    else {
                        this.alertService.showMessage("Logga in", `Sessionen för ${user.userName} har återställts!`, MessageSeverity.success);
                        setTimeout(() => {
                            this.alertService.showStickyMessage("Sessionen återställd", "V.v. försök igen", MessageSeverity.default);
                        }, 500);

                        this.closeModal();
                    }
                }, 500);
            },
            error: error => {
                this.alertService.stopLoadingMessage();

                if (Utilities.checkNoNetwork(error)) {
                  this.alertService.showStickyMessage(Utilities.noNetworkMessageCaption, Utilities.noNetworkMessageDetail, MessageSeverity.error, error);
                  this.offerAlternateHost();
                }
                else {
                    const errorMessage = Utilities.getHttpResponseMessage(error);

                    if (errorMessage) {
                        this.alertService.showStickyMessage("Kunde inte logga in", this.mapLoginErrorMessage(errorMessage), MessageSeverity.error, error);
                    }
                    else {
                        this.alertService.showStickyMessage("Kunde inte logga in", "Ett fel inträffade v.v försök senare.\nFelmeddelande: " + Utilities.stringify(error), MessageSeverity.error, error);
                    }
                }
                setTimeout(() => {
                    this.isLoading = false;
                }, 500);
            }
        });
    }


    offerAlternateHost() {

      if (Utilities.checkIsLocalHost(location.origin) && Utilities.checkIsLocalHost(this.configurations.baseUrl)) {

        var apiUrl = prompt("Dear Developer!\nIt appears your backend Web API service is not running...\n" +
          "Would you want to temporarily switch to the online Demo API below?(Or specify another)", this.configurations.fallbackBaseUrl);

        if (apiUrl) {
          this.configurations.baseUrl = apiUrl;
          this.alertService.showStickyMessage("API Changed!", "The target Web API has been changed to: " + apiUrl, MessageSeverity.warn);
        }
      }
    }


    mapLoginErrorMessage(error: string) {

        if (error == 'invalid_username_or_password')
            return 'Ogiltigt användarnamn eller lösenord';

        if (error == 'invalid_grant')
            return 'Kontot är inte aktivt';

        return error;
    }

    enterKeyDown() {
        this.enterKeyPress.emit();
    }

    get userName() { 
        return this.loginForm.controls.userName; 
    }

    get password() { 
        return this.loginForm.controls.password; 
    }
}
